import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { BellRing, ChevronRight, Bell, BellOff } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearAllNotifications,
  listNotifications,
  markAllRead,
  markRead,
  unreadCount,
  type Notification,
} from '@/api/notifications';
import { fireNotification, getNotificationPermission, requestNotificationPermission, type NotifyPermission } from '@/lib/notify';
import { BlockedHelpModal } from './NotificationPermissionChip';

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [perm, setPerm] = useState<NotifyPermission>(() => getNotificationPermission());
  const [showBlockedHelp, setShowBlockedHelp] = useState(false);

  // Re-read on focus in case the browser setting changed elsewhere.
  useEffect(() => {
    const onFocus = () => setPerm(getNotificationPermission());
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const onEnable = useCallback(async () => {
    if (perm !== 'default') return;
    const result = await requestNotificationPermission();
    setPerm(result);
    if (result === 'granted') {
      fireNotification('Notifications enabled', {
        body: 'You’ll get a desktop popup when a workflow step completes.',
        tag: 'welcome',
      });
    }
  }, [perm]);

  const { data: count = 0 } = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: unreadCount,
    refetchInterval: 60_000,
  });

  const { data: items = [] } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => listNotifications({ limit: 20, status: 'all' }),
    enabled: open,
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: clearAllNotifications,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((x) => !x)}
        className="relative flex h-9 w-9 items-center md:h-10 md:w-10 justify-center rounded-xl border topbar-btn text-[var(--text-secondary)] transition hover:text-accent"
        title="Notifications"
        aria-label="Notifications"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {count > 0 && (
          <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-danger px-1 text-center text-[9px] font-black text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {open && (
        // Viewport-anchored (fixed) so no topbar/flex ancestor can clip or
        // shift it. Mobile: full width with small side insets. sm+: a 380px
        // panel pinned to the top-right, clear of the taller desktop topbar.
        <div className="fixed left-2 right-2 top-14 z-[200] flex max-h-[80vh] flex-col overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-2xl sm:left-auto sm:right-4 sm:w-[380px] sm:max-w-[90vw] md:top-[72px]">
          <div className="flex items-center justify-between border-b border-[var(--border-color)] px-4 py-2.5">
            <div className="text-sm font-extrabold text-[var(--text-primary)]">Notifications</div>
            <div className="flex items-center gap-3">
              {perm === 'granted' && (
                <button
                  onClick={() =>
                    fireNotification('Test notification', {
                      body: 'If you see this in your Windows notification center, the pipeline is working.',
                      tag: 'test',
                    })
                  }
                  className="inline-flex items-center gap-1 rounded-md border border-success/30 bg-success/10 px-2 py-0.5 text-[11px] font-bold text-success hover:bg-success/20"
                  title="Send a test to your system notification center"
                >
                  <BellRing size={11} strokeWidth={2.5} /> Test
                </button>
              )}
              {items.length > 0 && count > 0 && (
                <button
                  onClick={() => markAllMutation.mutate()}
                  disabled={markAllMutation.isPending}
                  className="text-[11px] font-bold text-accent hover:underline disabled:opacity-50"
                >
                  Mark all read
                </button>
              )}
              {items.length > 0 && (
                <button
                  onClick={() => clearAllMutation.mutate()}
                  disabled={clearAllMutation.isPending}
                  className="text-[11px] font-bold text-danger hover:underline disabled:opacity-50"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
          {perm === 'denied' && (
            <button
              type="button"
              onClick={() => setShowBlockedHelp(true)}
              className="flex w-full items-center gap-2 border-b border-[var(--border-color)] bg-danger/[0.06] px-4 py-2 text-[11px] font-bold text-danger transition hover:bg-danger/10"
              title="Notifications are blocked — click for unblock steps"
            >
              <BellOff size={13} strokeWidth={2.25} /> Desktop notifications are blocked — how to unblock
            </button>
          )}
          {perm === 'default' && (
            <button
              type="button"
              onClick={onEnable}
              className="flex w-full items-center gap-2 border-b border-[var(--border-color)] bg-accent/[0.06] px-4 py-2 text-[11px] font-bold text-accent transition hover:bg-accent/10"
              title="Enable desktop notifications for step-complete alerts"
            >
              <Bell size={13} strokeWidth={2.25} /> Enable desktop notifications
            </button>
          )}
          <div className="max-h-[480px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[var(--text-muted)]">
                No notifications yet
              </div>
            ) : (
              items.map((n: Notification) => {
                const unread = n.status !== 'read';
                const handleClick = () => {
                  if (unread) markReadMutation.mutate(n.id);
                  if (n.actionHref) {
                    setOpen(false);
                    navigate(n.actionHref);
                  }
                };
                return (
                  <div
                    key={n.id}
                    onClick={handleClick}
                    className={clsx(
                      'cursor-pointer border-b border-white/[0.04] px-4 py-3 transition last:border-b-0',
                      unread ? 'bg-accent/[0.04] hover:bg-accent/10' : 'hover:bg-white/[0.03]',
                    )}
                  >
                    {n.subject && (
                      <div className={clsx('text-sm font-bold', unread ? 'text-accent' : 'text-[var(--text-primary)]')}>
                        {n.subject}
                      </div>
                    )}
                    <div className="mt-0.5 text-xs text-[var(--text-secondary)]">{n.body}</div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <div className="text-[10px] text-[var(--text-muted)]">{relativeTime(n.createdAt)}</div>
                      {n.actionHref && (
                        <div className="inline-flex items-center gap-0.5 text-[11px] font-bold text-accent">
                          Open <ChevronRight size={11} strokeWidth={2.5} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
      {showBlockedHelp && <BlockedHelpModal onClose={() => setShowBlockedHelp(false)} />}
    </div>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
